import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import { supabase } from "@/shared/lib/supabase";
import { EventType, trackEvent } from "@/shared/utils/core";
import { secondsSince } from "@/shared/utils/funnelAnalytics";

import { TriageModalSidebar } from "./TriageModalSidebar";

import scanDocsAnimation from "@/shared/assets/animations/scan_docs.lottie?url";

const POLL_INTERVAL_MS = 3000;
const MAX_WAIT_MS = 120_000;

export type ExtractionResult = "completed" | "partial" | "failed" | "timeout";

interface ExtractionRow {
  extraction_status: string | null;
  specific_doc_extraction: unknown;
  document_extraction: unknown;
}

interface ExtractionCheck {
  done: boolean;
  result: ExtractionResult;
  fieldsExtractedCount: number;
}

const DONE_STATUSES = ["completed", "failed", "partial"];

const countExtractedFields = (extraction: unknown): number =>
  extraction && typeof extraction === "object"
    ? Object.values(extraction).filter((v) => v !== null && v !== undefined && v !== "").length
    : 0;

const summarizeExtraction = (rows: ExtractionRow[]): ExtractionCheck => {
  const done =
    rows.length > 0 &&
    rows.every(
      (d) =>
        d.extraction_status === "failed" ||
        (DONE_STATUSES.includes(d.extraction_status ?? "") &&
          (d.specific_doc_extraction != null || d.document_extraction != null)),
    );
  const result: ExtractionResult = rows.every((d) => d.extraction_status === "completed")
    ? "completed"
    : rows.some((d) => d.extraction_status === "completed" || d.extraction_status === "partial")
      ? "partial"
      : "failed";
  const fieldsExtractedCount = Math.max(
    0,
    ...rows.map((d) => countExtractedFields(d.specific_doc_extraction ?? d.document_extraction)),
  );
  return { done, result, fieldsExtractedCount };
};

interface TriageModalScanningStepProps {
  personFirstName: string;
  estateId: number;
  documentIds?: number[];
  onComplete: () => void;
  onExtractionResult?: (result: ExtractionResult) => void;
}

export const TriageModalScanningStep: FC<TriageModalScanningStepProps> = ({
  personFirstName,
  estateId,
  documentIds,
  onComplete,
  onExtractionResult,
}) => {
  const [isDone, setIsDone] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onExtractionResultRef = useRef(onExtractionResult);
  onExtractionResultRef.current = onExtractionResult;

  const checkExtraction = useCallback(async (): Promise<ExtractionCheck> => {
    const { data } =
      documentIds && documentIds.length > 0
        ? await supabase
            .from("estate_documents")
            .select("extraction_status, specific_doc_extraction, document_extraction")
            .in("id", documentIds)
        : await supabase
            .from("estate_documents")
            .select("extraction_status, specific_doc_extraction, document_extraction")
            .eq("estate_id", estateId)
            .eq("document_type", "death_certificate")
            .order("created_at", { ascending: false })
            .limit(1);

    return summarizeExtraction((data ?? []) as ExtractionRow[]);
  }, [estateId, documentIds]);

  useEffect(() => {
    let isActive = true;
    let pollTimer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();
    let lastCheck: ExtractionCheck | null = null;

    const finish = (check: ExtractionCheck | null) => {
      if (!isActive) return;
      isActive = false;
      clearTimeout(pollTimer);
      const result: ExtractionResult = check?.done ? check.result : "timeout";
      trackEvent(EventType.DEATH_CERTIFICATE_EXTRACTION_COMPLETED, {
        estate_id: String(estateId),
        extraction_result: result,
        duration_seconds: secondsSince(startedAt),
        fields_extracted_count: check?.fieldsExtractedCount ?? 0,
      });
      onExtractionResultRef.current?.(result);
      setIsDone(true);
    };

    const poll = async () => {
      if (!isActive) return;
      const check = await checkExtraction();
      lastCheck = check;
      if (!isActive) return;

      if (check.done) {
        finish(check);
      } else {
        pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    const maxTimer = setTimeout(() => finish(lastCheck), MAX_WAIT_MS);

    return () => {
      isActive = false;
      clearTimeout(pollTimer);
      clearTimeout(maxTimer);
    };
  }, [checkExtraction, estateId]);

  useEffect(() => {
    if (isDone) {
      const timer = setTimeout(() => onCompleteRef.current(), 300);
      return () => clearTimeout(timer);
    }
  }, [isDone]);

  const personName = personFirstName || "the person who passed";

  return (
    <div className="flex h-full">
      <TriageModalSidebar />

      <div className="flex min-w-0 flex-1 flex-col items-center justify-center bg-subtle px-8">
        <div className="flex flex-col items-center text-center">
          <div className="h-[200px] w-[200px]">
            <DotLottieReact src={scanDocsAnimation} loop autoplay />
          </div>

          <h3 className="mt-4">Adding the details to {personName}'s record...</h3>
          <p className="mt-2 max-w-[400px] text-sm text-secondary">
            This usually takes a minute, so don't close this page. Everything you upload is
            encrypted and private.
          </p>
        </div>
      </div>
    </div>
  );
};
